import $ from "jquery";
import { isUndefined } from "underscore";
import * as Sentry from "sentry";
import getConfig from "next/config";
import { isServer } from "util/browser-utils";
import {
    allowList,
    blockList,
    getSentryEnvironment
} from "logger/sentry-utils";

const { publicRuntimeConfig } = getConfig();
const { SENTRY_KEYS, version } = publicRuntimeConfig;

const ignoredMessages = [
    `AbortError: The play() request was interrupted by a call to pause()`,
    `Maximum amount content exceeded.`,
    `No DVR content found for the provided stream and timestamps.`,
    `NotSupportedError: The element has no supported sources.`,
    `this.el_.vjs_getProperty is not a function`,
    `ResizeObserver loop limit exceeded`,
    `ResizeObserver loop completed with undelivered notifications.`,
    `Cannot read property 'removeChild' of null`,
    `AbortError: The operation was aborted.`,
    `Request failed with status code 401`,
    `App.Models.selectedStream.blocks is undefined`,
    `undefined is not an object (evaluating 'App.Models.selectedStream.blocks.isTimeWithinASegment')`,
    `NotSupportedError: The media resource indicated by the src attribute or assigned media provider object was not suitable.`,
    `NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.`
];

// A gist compiles a list of the most common browser errors
// Or generic 3rd party domains that are spammy for sentry
// https://gist.github.com/impressiver/5092952

const ignoredByDefault = [
    // Random plugins/extensions
    `top.GLOBALS`,
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    `originalCreateNotification`,
    `canvas.contentDocument`,
    `MyApp_RemoveAllHighlights`,
    `http://tt.epicplay.com`,
    `Can't find variable: ZiteReader`,
    `jigsaw is not defined`,
    `ComboSearch is not defined`,
    `http://loading.retry.widdit.com/`,
    `atomicFindClose`,
    // Facebook borked
    `fb_xd_fragment`,
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    `bmi_SafeAddOnload`,
    `EBCallBackMessageReceived`,
    // See http://Toolbarconduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    `conduitPage`,
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    `Script error.`
];

export const getSentryKey = (host, path) => {
    const keys = SENTRY_KEYS;

    let project;
    if (path.includes(`editor`)) {
        project = `editor`;
    } else if (path.includes(`producer`) || path.includes(`audio-mixer`)) {
        project = `producer`;
    } else if (path.includes(`login`)) {
        project = `login`;
    } else if (path.includes(`organization`)) {
        project = `organization`;
    } else if (path.includes(`campaign`)) {
        project = `campaign`;
    } else if (path.includes(`clipper`)) {
        project = `clipper`;
    } else if (path === `/` || path.includes(`home`)) {
        project = `home`;
    } else {
        project = `studio`;
    }

    if (allowList.includes(host)) {
        return keys[project];
    }
};

const isIgnored = (message) => {
    const messages = ignoredMessages.filter((ignoredMessage) =>
        message.includes(ignoredMessage)
    );
    return messages.length > 0;
};

export const initSentry = (host, path, jwtPayload) => {
    const dsn = getSentryKey(host, path);
    if (!isUndefined(dsn)) {
        const sentryConfig = {
            dsn,
            ignoreErrors: [...ignoredByDefault, ...ignoredMessages],
            allowUrls: [
                /grabyo\.com/,
                /grbydv\.com/,
                /dwo3ckksxlb0v\.cloudfront\.net/
            ],
            denyUrls: blockList,
            environment: getSentryEnvironment(host)
        };

        sentryConfig.release = `grabyo-${version}`;

        Sentry.init(sentryConfig);

        if (jwtPayload) {
            configureSentryScope(jwtPayload);
        }
    }
};

export const configureSentryScope = (jwtPayload) => {
    if (!jwtPayload) return;

    try {
        const scope = Sentry.getCurrentScope();
        const { sub: id, email = "@unknown", organizationId } = jwtPayload;
        const emailDomain = email.split("@")[1];
        scope.setUser({
            id,
            // We log the domain only to reduce the amount of personal information we log
            email: emailDomain
        });

        scope.setTag(`org_id`, organizationId);

        scope.setTag(`ssr`, isServer());

        if (!isServer()) {
            /**
             * jQuery AJAX error reporting
             * We don't want to get AJAX error reporting on the login page
             */
            $(document).ajaxError(function (
                event,
                jqXHR,
                ajaxSettings,
                thrownError
            ) {
                // Status of 500 and above are backend related
                // meaning that they shouldn't be reported in Sentry.
                const isClientError = jqXHR.status < 500;
                const isGrabyoUrl =
                    ajaxSettings.url.indexOf(`grabyo.com`) !== -1;
                if (isGrabyoUrl && isClientError) {
                    let errName;
                    let error;
                    if (jqXHR.responseJSON) {
                        error = jqXHR.responseJSON.error;
                    }
                    if (error?.errorMessage) {
                        errName = error.errorMessage;
                    } else {
                        errName = thrownError || jqXHR.statusText;
                    }

                    const response = (jqXHR.responseText || ``).substring(
                        0,
                        100
                    );

                    const extra = {
                        type: ajaxSettings.type,
                        url: ajaxSettings.url,
                        data: ajaxSettings.data,
                        status: jqXHR.status,
                        error: thrownError || jqXHR.statusText,
                        response
                    };

                    if (error) {
                        extra.errorMessage = error.errorMessage;
                        extra.errorCodeId = error.errorCodeId;
                    }

                    if (isIgnored(errName)) {
                        // If we don't match any error in the error names
                        Sentry.captureMessage(`[jqXHR] ${errName}`, {
                            extra
                        });
                    }
                }
            });
        }
    } catch (e) {
        console.error(e);
    }
};

export const captureException = (err) => {
    console.error(err);
    return Sentry.captureException(err);
};
